import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/roles/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantRoles.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const tenantRoles = {
  'tenantRoles.title': 'الأدوار والصلاحيات',
  'tenantRoles.audited': 'يُسجَّل كل تغيير هنا في سجل تدقيق المنصّة باسمك.',
  'tenantRoles.readOnly': 'رتبتك تتيح العرض فقط. تعديل الأدوار ومنح الصلاحيات يحتاج صلاحية «تعديل المنشآت».',
  'tenantRoles.configOnly': 'إعدادات لا محتوى: ما يحمله كل دور، لا ما كتبه أحد.',

  /* Sections */
  'tenantRoles.sections': 'أقسام الأدوار والصلاحيات',
  'tenantRoles.sectionRoles': 'الأدوار',
  'tenantRoles.sectionMatrix': 'مصفوفة الصلاحيات',
  'tenantRoles.dirtyFlag': 'فيها تعديلات غير محفوظة',

  /* Roles summary */
  'tenantRoles.rolesTitle': 'أدوار المنشأة',
  'tenantRoles.rolesCaption': 'أدوار المنشأة وما يحمله كل منها',
  'tenantRoles.columnRole': 'الدور',
  'tenantRoles.columnBase': 'الدور الأساسي',
  'tenantRoles.columnHolders': 'أعضاء على الدور الأساسي',
  'tenantRoles.columnCount': 'الصلاحيات',
  'tenantRoles.columnEditable': 'التعديل',
  'tenantRoles.lockSystem': 'نظامي — لا يُعدّل',
  'tenantRoles.lockUnknown': 'يحمل صلاحية غير معروفة',
  'tenantRoles.editable': 'قابل للتعديل',
  'tenantRoles.rolesEmptyTitle': 'لا أدوار في هذه المنشأة',
  'tenantRoles.rolesEmptyBody': 'كل منشأة تبدأ بثلاثة أدوار نظامية. غيابها خلل في التهيئة يستحق بلاغًا للفريق التقني.',

  /* Matrix */
  'tenantRoles.matrixTitle': 'مصفوفة الصلاحيات',
  'tenantRoles.matrixCaption': 'الصلاحيات حسب الدور',
  'tenantRoles.columnGroup': 'المجموعة',
  'tenantRoles.columnPermission': 'الصلاحية',
  'tenantRoles.systemHeader': '{name} (نظامي)',
  'tenantRoles.cellLabel': '{permission} في دور {role}',
  'tenantRoles.changed': 'معدّل',
  'tenantRoles.directOnlyNote': 'تُمنح لشخص بعينه لا لدور، وتمنحها إدارة المنشأة من داخلها.',
  'tenantRoles.opensContentNote':
    'لا تمنحها المنصّة لدور ولا لعضو لأنها تفتح محتوى الزملاء؛ تمنحها إدارة المنشأة نفسها. سحبها من هنا متاح.',
  'tenantRoles.cellLockedOpensContent': '{permission} في دور {role} — لا تمنحها المنصّة، تمنحها إدارة المنشأة',
  'tenantRoles.unknownPermission': 'رمز لا تعرفه هذه النسخة من اللوحة.',
  'tenantRoles.systemLocked':
    'الأدوار النظامية ({names}) معرّفة بالشكل نفسه في كل المنشآت، فلا تُعدّل من هنا. لتغيير ما يملكه شخص بعينه غيّر دوره من تبويب الأعضاء.',
  'tenantRoles.allLocked': 'لا دور قابلًا للتعديل في هذه المنشأة؛ المصفوفة للعرض.',
  'tenantRoles.unknownLocked':
    'الدور «{name}» يحمل صلاحية لا تعرفها هذه النسخة من اللوحة. الحفظ يرسل القائمة كاملة فقد يُسقطها، لذا أُقفل حتى تُحدَّث اللوحة.',
  'tenantRoles.catalogueFailed': 'تعذّر تحميل دليل الصلاحيات، فتُعرض الرموز كما يرسلها الخادم.',
  'tenantRoles.dirty': 'تعديلات غير محفوظة — الأدوار المعدّلة: {count}.',
  'tenantRoles.discard': 'تجاهل التعديلات',
  'tenantRoles.review': 'مراجعة وحفظ',

  /* Save confirm */
  'tenantRoles.saveTitle': 'حفظ صلاحيات الأدوار',
  'tenantRoles.saveConsequence': 'يسري ما يلي على كل من يحمل هذه الأدوار من طلبه التالي:',
  'tenantRoles.willGrant': 'سيُمنح: {list}',
  'tenantRoles.willRevoke': 'سيُسحب: {list}',
  'tenantRoles.saveSensitive':
    'من بين ما سيُمنح صلاحية تفتح محتوى الزملاء ({list}). كل من يحمل الدور سيقرأ محادثات زملائه في المنشأة.',
  'tenantRoles.saveConfirm': 'حفظ',
  'tenantRoles.saved': 'حُفظت الصلاحيات. الأدوار المحفوظة: {count}.',
  'tenantRoles.savedDropped': 'حُفظ دور «{name}» بـ{kept} من {sent} صلاحية؛ أسقط الخادم ما لا يعرفه.',
  'tenantRoles.savePartial': 'حُفظ بعض الأدوار. تعذّر حفظ «{name}»: {message}',
  'tenantRoles.error.opensContent':
    'رفض الخادم المنح: «{list}» تفتح محتوى الزملاء، والمنصّة لا تمنحها لدور ولا لعضو. تمنحها إدارة المنشأة نفسها من حسابها. سحبها من هنا متاح.',
  'tenantRoles.error.opensContentAny':
    'رفض الخادم المنح: الصلاحية تفتح محتوى الزملاء، والمنصّة لا تمنحها لدور ولا لعضو. تمنحها إدارة المنشأة نفسها من حسابها.',

  /* Leave guard */
  'tenantRoles.leaveTitle': 'تعديلات غير محفوظة',
  'tenantRoles.leaveConsequence': 'ستضيع تعديلات المصفوفة غير المحفوظة (الأدوار المعدّلة: {count}) ولن يُحفظ منها شيء.',
  'tenantRoles.leaveConfirm': 'المغادرة دون حفظ',

  /* Permission groups — `PermissionGroup` in the catalogue. */
  'tenantRoles.group.administration': 'الإدارة',
  'tenantRoles.group.members': 'الأعضاء',
  'tenantRoles.group.models': 'النماذج',
  'tenantRoles.group.audit': 'التدقيق',
  'tenantRoles.group.billing': 'التكاليف',
  'tenantRoles.group.content': 'المحتوى',
  'tenantRoles.group.knowledge': 'قاعدة المعرفة',
  'tenantRoles.group.cases': 'القضايا',
  'tenantRoles.group.review': 'المراجعة',
  'tenantRoles.group.other': 'أخرى',
} as const satisfies Messages<'tenantRoles'>;
